import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faBuilding, faCheckCircle, faTimesCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Button, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { ModalStatusServer } from '../../../components/ModalStatus/ModalStatusServer';
import { useLang } from '../../../language';
import { bumpSolicitudesRefreshKey } from '../../../store/slices/empresa';
import { EmpresasServices } from '../controllers/services/empresas.services';
import { ModalBuscarEmpresa } from '../ModalBuscarEmpresa';

interface ISolicitarEmpresa {
    /** Texto del disparador. Por defecto, `BUSCAR_AUTORIZAR_EMPRESA`. */
    label?: string
    /** Icono del disparador. Por defecto, el edificio de empresa. */
    icon?: IconDefinition
    /**
     * Disparador como texto + icono plano, sin fondo ni mayúsculas: el mismo aspecto que
     * los demás accesos de la cabecera. Por defecto, en cambio, es un botón MUI.
     */
    plain?: boolean
    variant?: 'text' | 'outlined' | 'contained'
    size?: 'small' | 'medium'
    color?: 'primary' | 'inherit'
}

/**
 * Flujo completo de "buscar y autorizar empresa": disparador + modal de búsqueda/alta +
 * los dos diálogos de estado.
 *
 * Vive aparte porque el disparador se pinta en dos sitios distintos: el botón de la
 * cabecera (`components/Layouts/LayoutPrimary/Navtop.tsx`), que es el único camino para
 * quien todavía no tiene ninguna empresa autorizada, y —comentado a propósito, ver
 * `pages/Empresas/index.tsx`— el botón del propio módulo.
 *
 * Cuando la solicitud o el alta se envían bien, en vez de refrescar un estado local avisa
 * al store (`bumpSolicitudesRefreshKey`), para que el listado se recargue aunque el envío
 * haya salido del módulo.
 */
export const SolicitarEmpresa = ({
    label,
    icon = faBuilding,
    plain = false,
    variant = 'contained',
    size = 'small',
    color = 'primary',
}: ISolicitarEmpresa) => {

    const Lang = useLang()

    const dispatch = useDispatch()

    const [openModal, setOpenModal] = useState(false)

    const [fetchSolicitarPOST, fetchSolicitarPOSTData] = EmpresasServices.POST_SOLICITAR()

    const [fetchRegistrarPOST, fetchRegistrarPOSTData, resetRegistrarPOST] = EmpresasServices.POST_REGISTRAR_EMPRESA()

    const textoBoton = label || Lang('BUSCAR_AUTORIZAR_EMPRESA')

    const handleAutorizar = (empresaId: number) => {

        fetchSolicitarPOST({
            body: {
                empresa_id: empresaId,
            }
        })
    }

    // Alta desde el modal de búsqueda: la empresa se crea y su solicitud de acceso
    // queda pendiente de que la empresa la acepte, así que el listado se refresca con
    // la misma clave que tras una autorización enviada.
    const handleRegistrar = (body: any) => {

        fetchRegistrarPOST({
            body
        })
    }

    useEffect(() => {

        if (String(fetchSolicitarPOSTData.status)?.[0] === '2') {

            setOpenModal(false)
            dispatch(bumpSolicitudesRefreshKey())
        }

    }, [fetchSolicitarPOSTData])

    useEffect(() => {

        if (String(fetchRegistrarPOSTData.status)?.[0] === '2') {

            setOpenModal(false)
            dispatch(bumpSolicitudesRefreshKey())
        }

    }, [fetchRegistrarPOSTData])

    return (
        <div>

            {
                plain
                    ?
                    /* Texto + icono plano, calcado del selector de idioma de la cabecera
                       (`Navtop`): sin fondo ni mayúsculas, hereda el color blanco de la barra. */
                    <Box
                        onClick={() => setOpenModal(true)}
                        aria-label={textoBoton}
                        title={textoBoton}
                        className='pointer'
                        sx={{ display: 'flex', alignItems: 'center', gap: 0.75, px: 1.5, whiteSpace: 'nowrap' }}
                    >
                        <FontAwesomeIcon icon={icon} style={{ fontSize: '1rem' }} />

                        <Typography
                            variant='body2'
                            /* En móvil la barra ya va justa (idioma + usuario + menú) y el
                               texto la desbordaría: ahí queda solo el icono, con el texto en
                               el `title`. Desde tablet se muestra el texto completo. */
                            sx={{ display: { xs: 'none', sm: 'block' }, fontSize: '0.875rem', lineHeight: 1 }}
                        >
                            {textoBoton}
                        </Typography>
                    </Box>
                    :
                    <Button
                        size={size}
                        color={color}
                        variant={variant}
                        startIcon={<FontAwesomeIcon icon={icon} />}
                        onClick={() => setOpenModal(true)}
                    >
                        {textoBoton}
                    </Button>
            }

            <ModalBuscarEmpresa
                open={openModal}
                onClose={() => setOpenModal(false)}
                onAutorizar={handleAutorizar}
                onRegistrar={handleRegistrar}
                registrarErrors={fetchRegistrarPOSTData.error?.errors}
                registrarLoading={!!fetchRegistrarPOSTData.loading}
            />

            <ModalStatusServer
                fetchData={fetchSolicitarPOSTData}
                activeLoading
                messageStatus={
                    {
                        200:
                            <>
                                <FontAwesomeIcon icon={faCheckCircle} className='color-green fa-2x' />

                                <Typography sx={{ mt: 2 }}>
                                    {fetchSolicitarPOSTData.data?.message || Lang('AUTORIZACION_SOLICITADA')}
                                </Typography>
                            </>,
                        201:
                            <>
                                <FontAwesomeIcon icon={faCheckCircle} className='color-green fa-2x' />

                                <Typography sx={{ mt: 2 }}>
                                    {fetchSolicitarPOSTData.data?.message || Lang('AUTORIZACION_SOLICITADA')}
                                </Typography>
                            </>,
                    }
                }
            />

            <ModalStatusServer
                fetchData={fetchRegistrarPOSTData}
                activeLoading
                // Al cerrar se limpia la respuesta del alta: si se deja, el diálogo se
                // volvería a abrir solo en la siguiente búsqueda.
                onClose={resetRegistrarPOST}
                messageStatus={
                    {
                        201:
                            <>
                                <FontAwesomeIcon icon={faCheckCircle} className='color-green fa-2x' />

                                <Typography sx={{ mt: 2 }}>
                                    {fetchRegistrarPOSTData.data?.message || Lang('EMPRESA_REGISTRADA')}
                                </Typography>

                                <Typography variant='body2' sx={{ mt: 1, color: 'text.secondary' }}>
                                    {Lang('EMPRESA_REGISTRADA_ESPERA')}
                                </Typography>
                            </>,
                        409:
                            <>
                                <FontAwesomeIcon icon={faTimesCircle} className='color-red fa-2x' />

                                <Typography sx={{ mt: 2 }}>
                                    {fetchRegistrarPOSTData.error?.error || Lang('EMPRESA_YA_REGISTRADA')}
                                </Typography>
                            </>,
                        422:
                            <>
                                <FontAwesomeIcon icon={faTimesCircle} className='color-red fa-2x' />

                                <Typography sx={{ mt: 2 }}>
                                    {Object.values(fetchRegistrarPOSTData?.error?.errors || {}).flat()[0] || Lang('ERROR_REGISTRAR_EMPRESA')}
                                </Typography>
                            </>,
                    }
                }
            />

        </div>
    )
}
